import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import InvoiceTable from "./components/InvoiceTable";
import Advance from "./components/Advance";
import Header from "./components/Header";
import IssueInfo from "./components/IssueInfo";
import generatePDF from 'react-to-pdf';
import SignatureBox from "./components/SignatureBox";
import OrdererForm from "./components/OrdererForm";
import InvoicePositionsForm from "./components/InvoicePositionsForm/InvoicePositionsForm";
import AdvanceForm from "./components/AdvanceForm";
import styled from "@emotion/styled";
import { useToast } from "../../hooks/useToast";
import InvoiceNumber from "./components/InvoiceNumber";
import InvoiceActionDialog from "./components/InvoiceActionDialog";
import { PDFDocument } from "pdf-lib";
import html2canvas from 'html2canvas';
import InvoicesController from "../../Api/invoicesController";
import logo from '../../assets/img/ufo.png'
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../../hooks/useUser";
import ZkZdStatusComponent from "./components/ZkZdStatusComponent";
import { IProforma } from "../../interfaces/IProforma";

const MenuBar = styled('div')`
    position: fixed;
    background: #ffffffff;
    width: 100%;
    top: 0;
    left: 0;
    height: 33px;
    z-index: 1000;
    box-shadow: 0 4px 24px 0 rgba(0,0,0,0.08);
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding-top: 4px;
    padding-bottom: 4px;
`


interface IParams {
    invoiceId?: string;
}


export interface IInvoiceData {
    number: number | 'auto';
    date: string;
    ordererName: string;
    ordererAddress: {
        street: string;
        houseNumber: string;
        apartmentNumber: string;
        postalCode: string;
        city: string;
    };
    createdBy: string;
    ordererPhone: string;
    ordererEmail: string;
    ordererNIP?: string;
    paymentTerm: number;
    advanceAmount: number;
    zk: string;
    zd: string;
    status: IProforma['status'];
}

export interface IInvoicePosition {
    name: string;
    description: string;
    quantity: number;
    grossPrice: number;
    vat: number;
    producer: string;
}

interface InvoicePageProps {

}

const InvoicePage: FC<InvoicePageProps> = () => {
    const [showActionDialog, setShowActionDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [invoiceData, setInvoiceData] = useState<IInvoiceData>({
        number: 'auto',
        date: new Date().toISOString().split('T')[0],
        ordererName: "",
        ordererAddress: {
            street: "",
            houseNumber: "",
            apartmentNumber: "",
            postalCode: "",
            city: ""
        },
        ordererNIP: "",
        ordererPhone: "",
        ordererEmail: "",
        paymentTerm: 7,
        advanceAmount: 0,
        zk: '',
        zd: '',
        status: 'awaiting_adv',
        createdBy: localStorage.getItem('invoiceName') || 'Nieznany',
    })
    const [invoicePositions, setInvoicePositions] = useState<IInvoicePosition[]>([
        {
            name: "",
            description: "",
            quantity: 1,
            grossPrice: 0,
            vat: 23,
            producer: ""
        }
    ]);
    const user = useUser();
    const toast = useRef(useToast()).current;
    const navigate = useNavigate();
    const params: IParams = useParams();


    const resetInvoiceData = () => {
        setInvoiceData({
            number: 'auto',
            date: new Date().toISOString().split('T')[0],
            ordererName: "",
            ordererAddress: {
                street: "",
                houseNumber: "",
                apartmentNumber: "",
                postalCode: "",
                city: ""
            },
            ordererNIP: "",
            ordererPhone: "",
            ordererEmail: "",
            paymentTerm: 7,
            advanceAmount: 0,
            zk: '',
            zd: '',
            status: 'awaiting_adv',
            createdBy: localStorage.getItem('invoiceName') || 'Nieznany',
        });
        setInvoicePositions([
            {
                name: "",
                description: "",
                quantity: 1,
                grossPrice: 0,
                vat: 23,
                producer: ""
            }
        ]);
    }


    const pushInvoice = async (invoiceNumber?: number) => {
        let invoiceNumberValue = invoiceData.number;
        if (invoiceNumber) invoiceNumberValue = invoiceNumber;
        if (invoiceNumberValue === 'auto') {
            setLoading(false);
            return toast.error('Nie udało się nadać numeru fakturze')
        }

        const res = await InvoicesController.createInvoice({
            number: invoiceNumberValue,
            date: invoiceData.date,
            createdBy: localStorage.getItem('invoiceName') ?? 'error',
            clientName: invoiceData.ordererName,
            clientNIP: invoiceData.ordererNIP,
            clientAddress: [invoiceData.ordererAddress],
            clientEmail: invoiceData.ordererEmail,
            clientPhone: invoiceData.ordererPhone,
            positions: invoicePositions,
            paymentTerm: invoiceData.paymentTerm,
            advanceAmmount: invoiceData.advanceAmount,
        })
        if (!res.ok) {
            setLoading(false);
            return toast.error(res.message)
        }
        navigate(`/proforma/${res.newInvoice._id}`, { replace: true });
        setLoading(false);
        toast.success("Faktura została utworzona");
        setShowActionDialog(true);
    }


    const handleEditInvoice = async (invoiceId: string) => {
        if (invoiceData.number === 'auto') return
        const res = await InvoicesController.editProforma({
            _id: invoiceId,
            number: invoiceData.number,
            date: invoiceData.date,
            createdBy: invoiceData.createdBy ?? 'error',
            clientName: invoiceData.ordererName,
            clientNIP: invoiceData.ordererNIP,
            clientAddress: [invoiceData.ordererAddress],
            clientEmail: invoiceData.ordererEmail,
            clientPhone: invoiceData.ordererPhone,
            positions: invoicePositions,
            paymentTerm: invoiceData.paymentTerm,
            advanceAmmount: invoiceData.advanceAmount,
            zk: invoiceData.zk,
            zd: invoiceData.zd,
            status: invoiceData.status
        });
        if (!res.ok) {
            setLoading(false);
            return toast.error(res.message || "Nie udało się edytować faktury");
        }
        setLoading(false);
        toast.success("Faktura została zaktualizowana");
        setShowActionDialog(true);
    }


    const handleSave = async () => {
        setLoading(true);
        if (params.invoiceId) return handleEditInvoice(params.invoiceId);
        let numberToUse = invoiceData.number;
        if (numberToUse === 'auto') {
            const year = invoiceData.date
                ? new Date(invoiceData.date).getFullYear()
                : new Date().getFullYear();

            const res = await InvoicesController.getNextInvoiceNumber(year);
            if (!res.ok || !res.nextNumber) {
                setLoading(false);
                return toast.error(res.message || "Nie udało się pobrać numeru faktury");
            }
            numberToUse = res.nextNumber;
            setInvoiceData(prev => ({ ...prev, number: numberToUse }));
        }
        await pushInvoice(numberToUse);
        setLoading(false);
    };


    const handleShowInvoice = () => {
        generatePDF(getTargetElement, options)
    }


    const getTargetElement = () => document.getElementById('content-id')


    const handleSendPDF = async (email: string) => {
        const target = getTargetElement();
        if (!target) {
            toast.error("Nie znaleziono elementu faktury do wygenerowania PDF.");
            return;
        }
        const canvas = await html2canvas(target, { scale: 6 });
        const imgData = canvas.toDataURL('image/png');
        const pdfDoc = await PDFDocument.create();
        const page = pdfDoc.addPage([595.28, 841.89]);
        const pngImage = await pdfDoc.embedPng(imgData);
        const { width, height } = page.getSize();
        page.drawImage(pngImage, {
            x: 0,
            y: 0,
            width,
            height,
        });
        const pdfBytes = await pdfDoc.save();
        const pdfBlob = new Blob([pdfBytes as any], { type: "application/pdf" });
        const res = await InvoicesController.sendInvoicePDF(pdfBlob, [email]);
        if (res.ok) {
            toast.success("Faktura została wysłana na email!");
        } else {
            toast.error(res.message || "Nie udało się wysłać faktury.");
        }
    };


    const safeDate = (date: string): Date => {
        const d = new Date(date);
        return isNaN(d.getTime()) ? new Date() : d;
    }

    const getPaymentDate = (date: string, days: number): string => {
        const d = safeDate(date);
        d.setDate(d.getDate() + days);
        return d.toISOString().split('T')[0];
    }

    const options: any = {
        method: "open",
    };


    const fillInputs = useCallback(async () => {
        setLoading(true);
        if (!params.invoiceId) {
            setLoading(false);
            return;
        }
        const res = await InvoicesController.getInvoiceById(params.invoiceId);
        if (!res.ok || !res.invoice) {
            setLoading(false);
            return toast.error(res.message || "Nie udało się pobrać faktury");
        }
        setInvoiceData({
            number: res.invoice.number,
            date: res.invoice.date
                ? new Date(res.invoice.date).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0],
            ordererName: res.invoice.clientName,
            ordererAddress: {
                street: res.invoice.clientAddress?.[0]?.street ?? "",
                houseNumber: res.invoice.clientAddress?.[0]?.houseNumber ?? "",
                apartmentNumber: res.invoice.clientAddress?.[0]?.apartmentNumber ?? "",
                postalCode: res.invoice.clientAddress?.[0]?.postalCode ?? "",
                city: res.invoice.clientAddress?.[0]?.city ?? "",
            },
            ordererNIP: res.invoice.clientNIP ?? "",
            ordererPhone: res.invoice.clientPhone ?? "",
            ordererEmail: res.invoice.clientEmail ?? "",
            paymentTerm: res.invoice.paymentTerm ?? 7,
            advanceAmount: res.invoice.advanceAmmount ?? 0,
            zk: res.invoice.zk ?? '',
            zd: res.invoice.zd ?? '',
            status: res.invoice.status ?? 'awaiting_adv',
            createdBy: res.invoice.createdBy || localStorage.getItem('invoiceName') || 'Nieznany',
        });
        setInvoicePositions(
            Array.isArray(res.invoice.positions) && res.invoice.positions.length > 0
                ? res.invoice.positions.map(pos => ({
                    name: pos.name ?? "",
                    description: pos.description ?? "",
                    quantity: pos.quantity ?? 1,
                    grossPrice: pos.grossPrice ?? 0,
                    vat: pos.vat ?? 23,
                    producer: pos.producer ?? "",
                }))
                : [{
                    name: "",
                    description: "",
                    quantity: 1,
                    grossPrice: 0,
                    vat: 23,
                    producer: "",
                }]
        );
        setLoading(false);
    }, [params.invoiceId, toast])


    useEffect(() => {
        fillInputs()
    }, [fillInputs])


    if (loading) return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
            }}
        >
            <CircularProgress color="primary" size={60} thickness={5} />
            <Typography variant="h6" sx={{ mt: 2 }}>
                Ładowanie...
            </Typography>
        </Box>
    );

    if (user.isAuthInvoice === undefined) return <></>

    if (user.isAuthInvoice === false) return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
            }}
        >
            <Typography variant="h4" sx={{ mb: 2 }}>
                Nie masz uprawnień do przeglądania tej strony.
            </Typography>
            <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/logowanieProformy')}
            >
                Zaloguj się
            </Button>
        </Box>
    )

    return (
        <>
            <InvoiceActionDialog
                open={showActionDialog}
                onClose={() => setShowActionDialog(false)}
                onPrint={handleShowInvoice}
                onSendToClient={handleSendPDF}
                onSendToShowroom={handleSendPDF}
                resetInvoiceData={resetInvoiceData}
            />
            <MenuBar>
                <IconButton
                    onClick={() => navigate('/proformy')}
                    sx={{
                        p: 1,
                        "&:hover": {
                            backgroundColor: "#ffee0052",
                        },
                    }}
                >
                    <img
                        onClick={() => navigate('/proformy')}
                        style={{ height: 28 }}
                        src={logo}
                        alt="Logo"
                    />
                </IconButton>
                <Button
                    color="success"
                    sx={{ mr: 3 }}
                    onClick={() => handleSave()}
                >
                    Zapisz
                </Button>
            </MenuBar>
            {params.invoiceId &&
                <>
                    <ZkZdStatusComponent
                        zk={invoiceData.zk}
                        zd={invoiceData.zd}
                        status={invoiceData.status}
                        setZk={(zk) => setInvoiceData({ ...invoiceData, zk })}
                        setZd={(zd) => setInvoiceData({ ...invoiceData, zd })}
                        setStatus={(status) => setInvoiceData({ ...invoiceData, status })}
                    />
                    <Box display="flex" justifyContent="center" width="100%">
                        <Box
                            boxShadow={5}
                            padding={4}
                            width="100%"
                            maxWidth="1086px"
                            mt={5}
                            borderRadius={3}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            bgcolor="#fafbfc"
                        >
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{ letterSpacing: 2, mb: 1 }}
                            >
                                Numer faktury
                            </Typography>
                            <Typography
                                variant="h4"
                                fontWeight={700}
                                mb={1}
                                textAlign="center"
                                color="primary.main"
                                sx={{ fontFamily: "monospace" }}
                            >
                                {invoiceData.number !== 'auto'
                                    ? `${invoiceData.number}/${String(safeDate(invoiceData.date).getMonth() + 1).padStart(2, "0")}/${safeDate(invoiceData.date).getFullYear()}`
                                    : 'auto'}
                            </Typography>
                        </Box>
                    </Box>
                </>
            }
            <OrdererForm
                invoiceData={invoiceData}
                setInvoiceData={setInvoiceData}
            />
            <InvoicePositionsForm
                invoicePositions={invoicePositions}
                setInvoicePositions={setInvoicePositions}
            />
            <AdvanceForm
                documentDate={invoiceData.date}
                paymentTerm={invoiceData.paymentTerm}
                setPaymentTerm={term => setInvoiceData({ ...invoiceData, paymentTerm: term })}
                advanceAmount={invoiceData.advanceAmount}
                setAdvanceAmount={amount => setInvoiceData({ ...invoiceData, advanceAmount: amount })}
            />
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                    backgroundColor: "#f0f0f0",
                }}
            >
                <Box
                    id="content-id"
                    sx={{
                        width: "210mm",
                        minHeight: "297mm",
                        margin: "0 auto",
                        background: "#fff",
                        p: '50px',
                        m: 2,
                    }}
                >
                    <IssueInfo date={invoiceData.date} />
                    <Header invoiceData={invoiceData} />
                    <InvoiceNumber
                        invoiceMonthAndYear={
                            `${String(safeDate(invoiceData.date).getMonth() + 1)
                                .padStart(2, "0")}/${safeDate(invoiceData.date).getFullYear()}`
                        }
                        invoiceNumber={invoiceData.number}
                    />
                    <InvoiceTable invoicePositions={invoicePositions} />
                    <Advance
                        advanceAmount={invoiceData.advanceAmount}
                        daysToPay={invoiceData.paymentTerm}
                        paymentDate={getPaymentDate(invoiceData.date, invoiceData.paymentTerm)}
                    />
                    <SignatureBox createdBy={invoiceData.createdBy} />
                </Box>
            </Box>
        </>
    )
}

export default InvoicePage;